import React, { Suspense, useCallback } from 'react';
import { filter } from 'lodash';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import { stopEventPropagation } from '@lyearn/core/utils/events';
import { Edit2Outline, PlusOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { IconButton, Stack, Tooltip, Typography, TypographyVariants } from '@lyearn/ui';
import { Link } from '@/components/Link';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import * as ActionItem from '@/modules/tasks/features/actionItems/components/ActionItem';
import ENTITY_TYPE_ENUM_MAP from '@/modules/tasks/features/actionItems/consts/entityTypeEnumMap';
import { useActionItemContext } from '@/modules/tasks/features/actionItems/context';
import { EntityTagInput } from '@/types/schema';

import useDashboardActions, { DashboardActions } from '../../../hooks/useDashboardActions';
import getInitialTask from '../../../utils/getInitalTasks';
import ActionDialogs from '../../ActionDialogs';
import SubtasksIcon from '../../TaskDashboard/TasksKanbanView/SubtasksIcon';

interface Props {
  variant?: TypographyVariants;
  childCount?: number;
  onViewTask?: () => void;
  inViewFileId?: string;
  taskLink?: string;
  disableInteractiveTooltip?: boolean;
  onExpand?: () => void;
}

function Title({
  variant,
  childCount = 0,
  onViewTask,
  inViewFileId,
  taskLink,
  disableInteractiveTooltip = false,
  onExpand,
}: Props) {
  const { inViewDialogState, dispatch, onClose } = useDashboardActions({});
  const { isMobile } = useScreen();

  const [isRenameMode, { toggle: toggleRenameMode, off: disableRenameMode }] = useToggle();

  const { id: currentUserId } = useUserContext();

  const { actionItem: parent } = useActionItemContext();

  const defaultAssigneeId = parent?.assignedToId;

  const resolvedUserId = defaultAssigneeId || currentUserId;

  const onAddNewTask = useCallback(() => {
    const { id, fileId, entityId, entityType } = parent;
    const entityTypeEnum = entityType ? ENTITY_TYPE_ENUM_MAP[entityType] : undefined;
    const userIds = filter(parent.assignedToIds) as string[];
    const userId = parent.assignedToId ?? resolvedUserId;

    dispatch({
      type: DashboardActions.ADD_NEW_TASK,
      payload: {
        task: {
          ...getInitialTask({
            parentId: id,
            fileId: fileId,
            userId: userIds.length ? userIds : [userId],
            entityId: entityId && entityTypeEnum ? entityId : undefined,
            entity: entityId && entityTypeEnum ? entityTypeEnum : undefined,
            statusId: parent.statusId,
            status: parent.status,
            category: parent.category,
            priority: parent.priority,
            dueDate: parent.dueDate,
            labelCategoryId: parent.labelCategoryId,
            tags: (parent?.tags || []) as EntityTagInput[],
          }),
        },
      },
    });
  }, [dispatch, parent, resolvedUserId]);

  const handleDialogClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleTitleClick = useCallback(
    (e: React.MouseEvent) => {
      if (onViewTask && !isMobile) {
        e.preventDefault();
        stopEventPropagation(e);
        onViewTask?.();
      }
    },
    [onViewTask, isMobile],
  );

  return (
    <>
      <Link
        className={cn('w-full group edit-bar-container', {
          'cursor-pointer': !isRenameMode,
        })}
        to={taskLink}
        onClick={handleTitleClick}>
        <Stack
          alignItems="center"
          className={cn('w-full group edit-bar-container', {
            'cursor-pointer': !isRenameMode,
          })}
          justifyContent="between">
          <Stack alignItems="center" className="w-full" direction="row" gapX={8}>
            <ActionItem.Identifier
              className="shrink-0 text-text-secondary"
              variant="body-short-03"
            />
            <ActionItem.Name
              disableRenameMode={disableRenameMode}
              isRenameMode={isRenameMode}
              numberOfLines={1}
              variant={variant}
            />
            {childCount ? (
              <Stack
                alignItems="center"
                className="cursor-pointer"
                direction="row"
                gapX={2}
                onClick={(e) => {
                  // these two lines are added to stop the modal opening
                  e.stopPropagation();
                  e.preventDefault();
                  onExpand?.();
                }}>
                <SubtasksIcon />
                <Typography className="text-text-placeholder" variant="body-short-03">
                  {childCount}
                </Typography>
              </Stack>
            ) : null}

            <Stack alignItems="center" className={cn('edit-bar', 'ml-auto')}>
              {isRenameMode ? null : (
                <Stack
                  className="flex-shrink-0 group-hover:flex hidden"
                  gap={8}
                  onClick={stopEventPropagation}>
                  <Tooltip disableInteractive={disableInteractiveTooltip} title="Add Subtask">
                    <IconButton
                      className="border border-border"
                      shape="square"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onAddNewTask();
                      }}>
                      <PlusOutline className="h-[1.6rem] w-[1.6rem]" height={16} width={16} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip disableInteractive={disableInteractiveTooltip} title="Rename">
                    <IconButton
                      className="border border-border"
                      shape="square"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleRenameMode();
                      }}>
                      <Edit2Outline className="h-[1.6rem] w-[1.6rem]" height={16} width={16} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
            <ActionItem.TaskProvider />
          </Stack>
        </Stack>
      </Link>

      <Suspense fallback={null}>
        <div onClick={handleDialogClick}>
          <ActionDialogs
            defaultAssigneeId={defaultAssigneeId || currentUserId}
            inViewDialogState={inViewDialogState}
            inViewFileId={inViewFileId}
            participants={[]}
            onClose={onClose}
            onViewTask={onViewTask}
          />
        </div>
      </Suspense>
    </>
  );
}

export default React.memo(Title);
